import React from 'react';
import {IconButton, INavLink, INavLinkGroup, INavStyles, mergeStyleSets, Nav, Stack, StackItem} from "@fluentui/react";
import { useHistory, useLocation } from "react-router-dom";
import useCan, {
    CATALOG_MODIFY,
    FOLIO_AUTHOR,
    ORG_EXTENSION_MODIFY,
    ORG_MODIFY,
    STOREFRONT_ADMIN,
    TAX_CODE_MODIFY
} from "../../permissions/ability";
import {useSelector} from "react-redux";
import {RootState} from "../../store/rootReducer";
import Footer from "../Layout/Footer";
import {useGetOrganizationEntitlementsQuery} from "../../store/endpoints/OrganizationsEndpoints";
import { useGetStorefrontAiGenerationsQuery, useGetStorefrontsQuery } from '../../store/endpoints/StorefrontSettingsEndpoints';
import { aiGenerationsByStorefront, storefrontsForNav } from '../Storefront/storefrontAiGeneration';

const SettingsLayout: React.FC = (props) => {
    const selectedOrganization = useSelector((state: RootState) => state.settings.selectedOrganization);
    const getOrgEntitlements = useGetOrganizationEntitlementsQuery(selectedOrganization ?? '', {skip: !selectedOrganization || selectedOrganization === ''});
    const history = useHistory();
    const location = useLocation();
    const navStyles: Partial<INavStyles> = { root: { width: '100%', overflowX: 'hidden' }};
    const canOrgModify = useCan(ORG_MODIFY);
    const canCatalogModify = useCan(CATALOG_MODIFY);
    const canTaxCodeModify = useCan(TAX_CODE_MODIFY);
    const canStorefrontAdmin = useCan(STOREFRONT_ADMIN);
    // useCan() calls useSelector, which makes it a hook: it has to run on every render, in the same order, so
    // these are read up here rather than at the point of use. Putting one inside a ternary branch below
    // meant it was called only when the other permission was held, and React counted a different number of
    // hooks from one render to the next.
    const canOrgExtensionModify = useCan(ORG_EXTENSION_MODIFY);
    const canAuthorFolios = useCan(FOLIO_AUTHOR);
    const storefronts = useGetStorefrontsQuery()
    // A store the assistant is still building is left out of this tree: its pages are being rewritten
    // as the user looks at them and most do not have their content yet, so a nav link into it leads
    // somewhere that reads as broken. It is still listed in the storefronts table, where the row says
    // what is happening and offers the way back into the generation.
    const aiGenerations = useGetStorefrontAiGenerationsQuery(undefined, {skip: !canStorefrontAdmin})
    const navStorefronts = React.useMemo(
        () => storefrontsForNav(storefronts.data?.items, aiGenerationsByStorefront(aiGenerations.data?.items)),
        [storefronts.data, aiGenerations.data]);

    const [mobileNavOpen, setMobileNavOpen] = React.useState(false);

    const classes = mergeStyleSets({
        container: {
            display: 'flex',
            alignItems: 'flex-start',
        },
        sideBar: {
            position: 'sticky',
            top: 'var(--app-header-offset)',
            height: 'calc(100vh - var(--app-header-offset))',
            boxSizing: 'border-box',
            width: '300px',
            minWidth: '300px',
            overflowY: 'auto',
            overflowX: 'hidden',
            borderRight: '1px solid #edebe9',
            padding: '0.5em',
            '@media(max-width: 600px)': mobileNavOpen ? {
                display: 'flex',
                flexDirection: 'column',
                position: 'fixed',
                top: 'var(--app-header-offset)',
                left: 0,
                right: 0,
                bottom: 0,
                width: '100%',
                minWidth: 0,
                height: 'calc(100vh - var(--app-header-offset))',
                zIndex: 9999,
                backgroundColor: 'white',
                borderRight: 'none',
                padding: '0',
            } : {
                display: 'none',
            },
        },
        mobileSidebarHeader: {
            display: 'none',
            '@media(max-width: 600px)': {
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                padding: '0.5em',
                borderBottom: '1px solid #edebe9',
                flexShrink: 0,
            },
        },
        mobileNavScrollArea: {
            '@media(max-width: 600px)': {
                flex: 1,
                overflowY: 'auto',
                padding: '0.5em',
            },
        },
        mobileMenuBar: {
            display: 'none',
            '@media(max-width: 600px)': {
                display: 'flex',
                alignItems: 'center',
                padding: '0.25em 0.5em',
                borderBottom: '1px solid #edebe9',
                marginBottom: '0.5em',
            },
        },
        content: {
            flex: 1,
            minWidth: 0,
            paddingTop: '1em',
            paddingLeft: '1em',
            paddingRight: '1em',
            height: 'calc(100vh - var(--app-header-offset))',
            boxSizing: 'border-box',
            overflowY: 'auto',
            '@media(max-width: 600px)': {
                paddingTop: 0,
                paddingLeft: '1em',
                paddingRight: '1em',
            },
        }
    });

    const orgNavLinks: INavLink[] = [];
    
    if (canOrgModify) {
        orgNavLinks.push({
            key: 'Organization',
                name: 'Organization',
            icon: 'CityNext2',
            url: '/settings/organization',
        });
    }
    orgNavLinks.push({
        key: 'Search Settings',
        name: 'Search Settings',
        icon: 'Search',
        url: '/settings/search',
    })
    if (canOrgModify) {
        orgNavLinks.push({
            key: 'Vendor Management',
            name: 'Vendor Management',
            icon: 'DeliveryTruck',
            url: '/settings/vendors',
        })
    }
    if (canCatalogModify) {
        orgNavLinks.push({
            key: 'Catalog Management',
            name: 'Catalog Management',
            icon: 'ProductCatalog',
            url: '/settings/catalogs',
        });
    }
    orgNavLinks.push({
        key: 'Category Management',
        name: 'Category Management',
        icon: 'TripleColumn',
        url: '/settings/categories',
    });
    orgNavLinks.push({
        key: 'My User',
        name: 'My User',
        icon: 'ContactInfo',
        url: '/settings/myUser',
    });
    orgNavLinks.push({
        key: 'User Management',
        name: 'User Management',
        icon: 'AccountManagement',
        url: '/settings/users',
    });
    if(canTaxCodeModify) {
        orgNavLinks.push({
            key: 'Tax Code Management',
            name: 'Tax Code Management',
            icon: 'Money',
            url: '/settings/taxCodes',
        });
    }
    if (getOrgEntitlements.data?.subOrganizationsEnabled === true) {
        orgNavLinks.push({
            key: 'Sub Organization Management',
            name: 'Sub Organization Management',
            icon: 'Org',
            url: '/settings/subOrganizations',
        });
    }
    orgNavLinks.push({
        key: 'Contact Management',
        name: 'Contact Management',
        icon: 'Contact',
        url: '/settings/contacts',
    });
    // Internal Adaptive Catalog staff tool - gated on an org entitlement, not shown or explained to
    // tenants without it. Matches the loading behavior of the subOrganizationsEnabled link above:
    // getOrgEntitlements.data is undefined while loading, so the link simply doesn't appear yet
    // rather than flashing then disappearing.
    if (getOrgEntitlements.data?.settingsReferenceEnabled === true) {
        orgNavLinks.push({
            key: 'Settings Reference',
            name: 'Settings Reference',
            icon: 'DocumentSearch',
            url: '/settings/reference',
        });
    }
    
    const extensionManagementLink: INavLink = {
            key: 'Extension Management',
            name: 'Extension Management',
            icon: 'Link',
            url: '/settings/extensions',
    }
    const apiKeysLink: INavLink = {
        key: 'API Keys',
        name: 'API Keys',
        icon: 'Lock',
        url: '/settings/apikeys',
    };
    const webhooksLink: INavLink = {
        key: 'Webhooks',
        name: 'Webhooks',
        icon: 'Flow',
        url: '/settings/webhooks',
    };
    // Authoring connector definitions, as opposed to installing them. Entitlement-gated server side, so
    // the claim's absence is the whole check — the same one the Settings home card uses.
    const folioEditorLink: INavLink = {
        key: 'Extension Folios',
        name: 'Extension Folios',
        icon: 'Trackers',
        url: '/settings/folios',
    };
    const extensionNavLinks: INavLink[] = canOrgExtensionModify  ?
        [extensionManagementLink, ...(canAuthorFolios ? [folioEditorLink] : []), apiKeysLink, webhooksLink ] :
        [apiKeysLink];
    const isGroupActive = (links: INavLink[]) => links.some(link => location.pathname.startsWith(link.url));
    const isStorefrontActive = (storefrontId: string) => location.pathname.startsWith(`/settings/storefront/storefronts/${storefrontId}`);

    const selectedKey = React.useMemo(() => {
        const storefrontMatch = location.pathname.match(/^\/settings\/storefront\/storefronts\/([^/]+)(?:\/(\w+))?/);
        if (storefrontMatch) {
            const [, id, subPage] = storefrontMatch;
            switch (subPage) {
                case 'general': return `storefront-${id}-settings`;
                case 'pages': return `storefront-${id}-pages`;
                case 'ordering': return `storefront-${id}-ordering`;
                case 'products': return `storefront-${id}-products`;
                case 'integrations': return `storefront-${id}-integrations`;
                case 'authentication': return `storefront-${id}-authentication`;
                case 'users': return `storefront-${id}-users`;
                default: return `storefront-${id}`;
            }
        }
        return undefined;
    }, [location.pathname]);
    const navLinkGroups: INavLinkGroup[] = [
        {
            name: 'Organization',
            links: orgNavLinks,
            collapseByDefault: !isGroupActive(orgNavLinks),
        },
        {
            name: 'Extensions',
            links: extensionNavLinks,
            collapseByDefault: !isGroupActive(extensionNavLinks),
        },
    ];

    if (canStorefrontAdmin) {
        const storefrontNavLinks: INavLink[] = [
            {
                key: 'Storefront Dashboard',
                name: 'Dashboard',
                icon: 'Shop',
                url: '/settings/storefront/dashboard',
            },
            {
                key: 'Storefront Ordering & Carts',
                name: 'Ordering & Carts',
                icon: 'ShoppingCart',
                url: '/settings/storefront/ordering',
            },
            {
                key: 'Storefront Products',
                name: 'Products',
                icon: 'Product',
                url: '/settings/storefront/products',
            },
            {
                key: 'Storefront Integrations',
                name: 'Integrations',
                icon: 'Link',
                url: '/settings/storefront/integrations',
            },
            {
                key: 'Storefront Appearance',
                name: 'Appearance',
                icon: 'Design',
                url: '/settings/storefront/appearance',
            },
            {
                key: 'Storefront Assets',
                name: 'Assets',
                icon: 'Photo2',
                url: '/settings/storefront/assets',
            },
            {
                key: 'Storefronts',
                name: 'Storefronts',
                isExpanded: true,
                url: '/settings/storefront/storefronts',
                links: storefronts.data
                    ? navStorefronts.map(storefront => ({
                        key: `storefront-${storefront.id}`,
                        name: storefront.name ?? '',
                        url: `/settings/storefront/storefronts/${storefront.id}/dashboard`,
                        isExpanded: isStorefrontActive(storefront.id ?? ''),
                        links: [
                            {
                                key: `storefront-${storefront.id}-settings`,
                                name: 'General Settings',
                                url: `/settings/storefront/storefronts/${storefront.id}/general`,
                                icon: 'Settings',
                            },
                            {
                                key: `storefront-${storefront.id}-pages`,
                                name: 'Pages',
                                url: `/settings/storefront/storefronts/${storefront.id}/pages`,
                                icon: 'Page',
                            },
                            {
                                key: `storefront-${storefront.id}-localization`,
                                name: 'Localization',
                                url: `/settings/storefront/storefronts/${storefront.id}/localization`,
                                icon: 'LocaleLanguage',
                            },
                            {
                                key: `storefront-${storefront.id}-ordering`,
                                name: 'Ordering & Carts',
                                url: `/settings/storefront/storefronts/${storefront.id}/ordering`,
                                icon: 'ShoppingCart',
                            },
                            {
                                key: `storefront-${storefront.id}-products`,
                                name: 'Products',
                                url: `/settings/storefront/storefronts/${storefront.id}/products`,
                                icon: 'Product',
                            },
                            {
                                key: `storefront-${storefront.id}-integrations`,
                                name: 'Integrations',
                                url: `/settings/storefront/storefronts/${storefront.id}/integrations`,
                                icon: 'Link',
                            },
                            {
                                key: `storefront-${storefront.id}-authentication`,
                                name: 'Authentication',
                                url: `/settings/storefront/storefronts/${storefront.id}/authentication`,
                                icon: 'Lock',
                            },
                            {
                                key: `storefront-${storefront.id}-users`,
                                name: 'Users',
                                url: `/settings/storefront/storefronts/${storefront.id}/users`,
                                icon: 'People',
                            }
                        ]
                    }))
                    : [
                        {
                            key: 'storefront-placeholder',
                            name: 'Loading',
                            url: '#',
                            isDisabled: true,
                        }
                    ],
            }
        ];
        navLinkGroups.push({
            name: 'Storefront',
            links: storefrontNavLinks,
            collapseByDefault: !isGroupActive(storefrontNavLinks),
        });
    }
    if (getOrgEntitlements.data?.apiUsageBillingEnabled) {
        const reportingLinks: INavLink[] = [
            {
                key: 'API Usage',
                name: 'API Usage',
                icon: 'ReportDocument',
                url: '/settings/reporting/api',
            },
        ];
        navLinkGroups.push({
            name: 'Reporting',
            links: reportingLinks,
            collapseByDefault: !isGroupActive(reportingLinks),
        });
    }
    return (
    <div className={classes.container}>
        <div className={classes.sideBar}>
            <div className={classes.mobileSidebarHeader}>
                <span style={{ fontWeight: 600, fontSize: '1em' }}>Navigation</span>
                <IconButton
                    iconProps={{ iconName: 'Cancel' }}
                    title="Close navigation"
                    onClick={() => setMobileNavOpen(false)}
                />
            </div>
            <div className={classes.mobileNavScrollArea}>
                <Nav
                    groups={navLinkGroups}
                    styles={navStyles}
                    selectedKey={selectedKey}
                    onLinkClick={(e, l) => {
                        e?.preventDefault();
                        setMobileNavOpen(false);
                        if (l?.url)
                            history.push(l?.url);
                    }}
                />
            </div>
        </div>
        <div className={classes.content}>
            <div className={classes.mobileMenuBar}>
                <IconButton
                    iconProps={{ iconName: 'GlobalNavButton' }}
                    title="Open navigation"
                    onClick={() => setMobileNavOpen(true)}
                />
            </div>
            {props.children}
            <Footer className={'powered-by-footer-settings-page'}/>
        </div>
    </div>
    )
}

export default SettingsLayout;